'use client';

import type { VariantProps } from 'class-variance-authority';
import { usePathname } from 'next/navigation';

import type { IconType } from '@/app/components/icons';
import { Icon } from '@/app/components/icons';
import { IconOpenInNew } from '@/app/components/icons/ui-actions/icon-open-in-new';
import { ContainerStyled } from '@/app/components/layouts/container';
import {
  TOP_NAV_DATA_GA,
  TOP_NAV_DATA_TEST,
} from '@/app/components/layouts/top-navigation/tracking';
import { Link } from '@/app/components/navigation/link';
import type { MenuLinkType } from '@/app/types/top-navigation';
import { cnva } from '@/app/utils/cnva';
import { createDataAttributes } from '@/app/utils/createDataAttributes';
import { createDataGA } from '@/app/utils/createDataGA';
import { stripHtmlTags } from '@/app/utils/format';

export const MenuItemStyled = cnva(
  'transition-color relative flex cursor-pointer items-center py-1.5 text-left outline-none hover:text-red-500 focus-visible:text-red-500',
  {
    variants: {
      noContainer: {
        true: '',
        false: ContainerStyled({ size: 'mobile-only', maxWidth: 'none' }),
      },
      isOpenNewTab: {
        true: 'gap-x-2',
        false: '',
      },
      hasIcon: {
        true: 'justify-between gap-x-2',
        false: '',
      },
    },
    defaultVariants: {
      isOpenNewTab: false,
      noContainer: false,
      hasIcon: false,
    },
  }
);

interface MenuItemProps extends VariantProps<typeof MenuItemStyled> {
  item: MenuLinkType;
  icon?: IconType;
  className?: string;
  onClickNext?: () => void;
  onClose?: () => void;
}

const getLabelGaText = (item: MenuLinkType) =>
  item.labelHtml ? stripHtmlTags(item.labelHtml) : item.label;

const Label = ({ item }: { item: MenuLinkType }) => {
  return item.labelHtml ? (
    <div
      className="where-span:text-inline-wrap"
      dangerouslySetInnerHTML={{ __html: item.labelHtml }}
    />
  ) : (
    item.label
  );
};

export const MenuItem = ({
  item,
  icon,
  noContainer = false,
  className,
  onClickNext,
  onClose,
}: MenuItemProps) => {
  const pathname = usePathname();
  const styleConfigs = {
    hasIcon: !!icon,
    noContainer,
    className,
  };

  if (typeof onClickNext === 'function' || pathname === item.href) {
    return (
      <button
        className={MenuItemStyled(styleConfigs)}
        role="menuitem"
        {...createDataAttributes({
          test: TOP_NAV_DATA_TEST.navigationMenuLink,
        })}
        {...createDataGA({
          click: true,
          eventName: TOP_NAV_DATA_GA.topNavigation,
          text: getLabelGaText(item),
        })}
        onClick={pathname === item.href ? onClose : onClickNext}
      >
        <Label item={item} />
        {!!icon && <Icon icon={icon} />}
      </button>
    );
  }

  return (
    <Link
      href={item.href}
      target={item.target}
      role="menuitem"
      prefetch={null}
      {...createDataAttributes({
        test: TOP_NAV_DATA_TEST.navigationMenuLink,
      })}
      {...createDataGA({
        click: true,
        eventName: TOP_NAV_DATA_GA.topNavigation,
        text: getLabelGaText(item),
        url: item.href,
      })}
      className={MenuItemStyled({
        ...styleConfigs,
        isOpenNewTab: item.target === '_blank',
      })}
    >
      <Label item={item} />
      {item.target === '_blank' ? (
        <Icon icon={<IconOpenInNew />} />
      ) : (
        !!icon && <Icon icon={icon} />
      )}
    </Link>
  );
};
